import { fetchExperienceSearchList } from 'actions/ExperienceActions'
import { EmptyExperienceList } from 'constants/experience'
import useMountedEffect from 'hooks/useMountedEffect'
import arrayShuffle from 'lib/array/arrayShuffle'
import { EmptyArray, without } from 'lib/array/arrayUtils'
import { getExperienceListKey, type ExperienceCoordinates } from 'lib/experiences/experienceUtils'
import memoize from 'lib/memoize/memoize'
import { useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

/**
 * Memoize is used here as it provides a stable set of offer ids for that exact version of the array
 * This ensures that we always return the same randomised set throughout the life of the page
 */
const getRandomizedOfferIds = memoize((offerIds: Array<string>) => arrayShuffle(offerIds))

interface Props {
  categoryCodes?: Array<number>
  placeId?: string
  fallbackPlaceIds?: Array<string>
  placeIdToIgnore?: string
  sortBy?: Experiences.SortTypes
  excludeIds?: Array<string>
  campaigns?: Array<string>
  priceLte?: number
  priceGte?: number
  randomize?: boolean
  enabled?: boolean
  bounds?: string
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
  leExclusive?: boolean
  collections?: Array<string>
  from?: string
  to?: string
  minRating?: number
  targetAudience?: Array<App.ExperienceTargetAudience>
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  isGift?: boolean
  coordinatesList?: Array<ExperienceCoordinates>
}

function useExperienceSearchList({
  categoryCodes,
  placeId,
  fallbackPlaceIds = EmptyArray,
  placeIdToIgnore,
  sortBy,
  priceLte,
  priceGte,
  excludeIds = EmptyArray,
  campaigns,
  randomize,
  bounds,
  enabled = true,
  luxPlusFeatures,
  leExclusive,
  collections,
  from,
  to,
  minRating,
  targetAudience,
  hasFreeCancellation,
  hasInstantConfirmation,
  isGift,
  coordinatesList,
}: Props): App.ApiCallState<{ ids: Array<string>, placeId: string | undefined, listKey: string }> {
  const dispatch = useAppDispatch()
  const [currentPlaceId, setPlaceId] = useState(placeId)

  useMountedEffect(() => {
    // if the place ID changes, set it and restart
    setPlaceId(placeId)
  }, [placeId])

  const listKey = useMemo(() => {
    return getExperienceListKey({ placeId: currentPlaceId, categoryCodes, sortBy, priceLte, priceGte, campaigns, bounds, luxPlusFeatures, leExclusive, collections, from, to, minRating, targetAudience, hasFreeCancellation, hasInstantConfirmation, isGift, coordinatesList })
  }, [currentPlaceId, categoryCodes, sortBy, priceLte, priceGte, campaigns, bounds, luxPlusFeatures, leExclusive, collections, from, to, minRating, targetAudience, hasFreeCancellation, hasInstantConfirmation, isGift, coordinatesList])
  const experienceList = useAppSelector((state) => state.experience.experienceLists[listKey] ?? EmptyExperienceList)

  useEffect(() => {
    if ((currentPlaceId || bounds || coordinatesList?.length) && enabled) {
      dispatch(fetchExperienceSearchList(currentPlaceId, { placeIdToIgnore, categoryCodes, sortBy, priceLte, priceGte, campaigns, bounds, luxPlusFeatures, leExclusive, collections, from, to, minRating, targetAudience, hasFreeCancellation, hasInstantConfirmation, isGift, coordinatesList }))
    }
  }, [currentPlaceId, placeIdToIgnore, categoryCodes, sortBy, priceLte, priceGte, campaigns, bounds, luxPlusFeatures, leExclusive, enabled, dispatch, collections, from, to, minRating, targetAudience, hasFreeCancellation, hasInstantConfirmation, isGift, coordinatesList])

  useEffect(() => {
    if (fallbackPlaceIds.length > 0) {
      if (!experienceList?.fetching && experienceList.ids.length === 0) {
        // couldn't find anything...move onto our fallbacks (or the next fallback)
        const existingFallbackIndex = fallbackPlaceIds.findIndex(id => id === currentPlaceId)

        const nextPlaceId = fallbackPlaceIds[existingFallbackIndex + 1]
        if (nextPlaceId) {
          setPlaceId(nextPlaceId)
        }
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceList, fallbackPlaceIds])

  const list = useMemo<App.ApiCallState<{ ids: Array<string>, placeId: string | undefined, listKey: string }>>(() => {
    if (!randomize && excludeIds.length === 0) {
      return {
        ...experienceList,
        placeId: currentPlaceId,
        listKey,
      }
    }

    let ids = experienceList.ids
    if (randomize) {
      ids = getRandomizedOfferIds(experienceList.ids)
    }

    return {
      ...experienceList,
      ids: excludeIds.length > 0 ? without(ids, ...excludeIds) : ids,
      placeId: currentPlaceId,
      listKey,
    }
  }, [randomize, excludeIds, experienceList, currentPlaceId, listKey])

  return list
}

export default useExperienceSearchList
